import { useState } from "react";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createBlogAuthorMutation,
  BlogAuthorListItem,
} from "@/app/Blogs/GraphQL/blogAuthorQueries";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { BlogAuthorForm } from "@/app/Blogs/Forms/BlogAuthorForm";
import { blogAuthorValidation } from "@/app/Blogs/Validation/blogValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import {
  CreateBlogAuthorInput,
  MediaCollection,
  ModelType,
} from "@/gql/graphql";
import { FileUploader, Upload } from "@/lib/FileUpload/FileUploader";
import { CheckCircle, XCircle } from "lucide-react";

type BlogAuthorCreateDialogProps = {
  initialValues?: Partial<CreateBlogAuthorInput>;
};

export const BlogAuthorCreateDialog: DialogComponent<
  BlogAuthorCreateDialogProps,
  BlogAuthorListItem
> = ({ initialValues }) => {
  const { close, resolve } = useDialogState<BlogAuthorListItem>();
  const { mutateAsync: createAuthor } = useGqlMutation(
    createBlogAuthorMutation,
  );
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const [pendingUpload, setPendingUpload] = useState<Upload | null>(null);

  return (
    <BaseDialog title="Add author">
      <GenericForm<CreateBlogAuthorInput>
        validationSchema={toFormikValidationSchema(blogAuthorValidation)}
        initialValues={{
          name: "",
          role: null,
          bio: null,
          ...initialValues,
        }}
        onSubmit={async (values) => {
          const res = await createAuthor({ input: values });

          if (pendingUpload) {
            await createMedia({
              input: {
                file_name: pendingUpload.file.name,
                objectId: res.createBlogAuthor.id,
                objectType: ModelType.BlogAuthor,
                collectionName: MediaCollection.ProfilePicture,
                key: pendingUpload.filePath,
              },
            });
          }

          resolve?.(res.createBlogAuthor);
          await close();
        }}
      >
        <BlogAuthorForm />
        <div className="col-span-12 space-y-2">
          <label className="block text-sm font-medium text-zinc-700">
            Profile picture
          </label>
          {pendingUpload ? (
            <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
              <span className="flex items-center gap-2 text-zinc-700">
                <CheckCircle className="size-4 text-green-600" />
                {pendingUpload.file.name}
              </span>
              <button
                type="button"
                onClick={() => setPendingUpload(null)}
                className="text-zinc-500 hover:text-zinc-700"
                aria-label="Remove upload"
              >
                <XCircle className="size-4" />
              </button>
            </div>
          ) : (
            <FileUploader
              maxFileCount={1}
              onUpload={(uploads) => {
                if (uploads[0]) setPendingUpload(uploads[0]);
              }}
            />
          )}
        </div>
        <DialogSubmitButton text="Add author" />
      </GenericForm>
    </BaseDialog>
  );
};
